import { createFont, isWeb } from 'tamagui';

export enum FontFamilyName {
    FigtreeMedium = 'Figtree-Medium',
    FigtreeSemiBold = 'Figtree-SemiBold',
    FigtreeMediumItalic = 'Figtree-MediumItalic',
    FigtreeSemiBoldItalic = 'Figtree-SemiBoldItalic',
    FigtreeVariable = 'Figtree-Variable', // TODO: Replace with specific italic font weights for web. For web only, for more details on web italics see useCustomFonts.web.tsx
    IosevkaExtendedMedium = 'IosevkaExtended-Medium',
    IosevkaExtendedSemiBold = 'IosevkaExtended-SemiBold',
    SourceSerifRegular = 'SourceSerif4-Regular',
    SourceSerifSemiBold = 'SourceSerif4-SemiBold',
    SourceSerifBold = 'SourceSerif4-Bold',
    SourceSerifItalic = 'SourceSerif4-Italic',
}

// The ideal set to support all common OSs and browsers
// Source https://fontsarena.com/blog/operating-systems-default-sans-serif-fonts/
const WEBFONT_FALLBACKS =
    'system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, "Helvetica Neue", sans-serif';
const MONO_WEBFONT_FALLBACKS = 'monospace';

export const getDefaultFontFamily = (): string => {
    // Native devices do not support a comma separated list of font names (for fallbacks)
    // However, for web this is useful so that our UI will still look as intended while the
    // custom font is loading or if something prevents it from loading
    // return !isWeb
    //     ? (FontFamilyName.PPNeueMontrealMedium as string)
    //     : `${FontFamilyName.PPNeueMontrealVariable}, ${WEBFONT_FALLBACKS}`;
    return !isWeb
        ? (FontFamilyName.FigtreeMedium as string)
        : `${FontFamilyName.FigtreeVariable}, ${WEBFONT_FALLBACKS}`;
};

export const getDefaultMonoFontFamily = (): string => {
    // Native devices do not support a comma separated list of font names (for fallbacks)
    // However, for web this is useful so that our UI will still look as intended while the
    // custom font is loading or if something prevents it from loading
    return !isWeb
        ? (FontFamilyName.IosevkaExtendedMedium as string)
        : `${FontFamilyName.IosevkaExtendedMedium}, ${MONO_WEBFONT_FALLBACKS}`;
};

export const getDefaultMonoEmphasizedFontFamily = (): string => {
    // Native devices do not support a comma separated list of font names (for fallbacks)
    // However, for web this is useful so that our UI will still look as intended while the
    // custom font is loading or if something prevents it from loading
    return !isWeb
        ? (FontFamilyName.IosevkaExtendedSemiBold as string)
        : `${FontFamilyName.IosevkaExtendedSemiBold}, monospace`;
};

const fontWeightTokens = isWeb
    ? // Web weights are different from native because we are using a variable weight font face for web
      // rather than defaulting to heavier weight font faces for native.
      {
          1: '450',
          2: '600',
          3: '700',
      }
    : {
          1: '500',
          2: '600',
          3: '700',
      };

const bodyFontConfig = {
    family: getDefaultFontFamily(),
    size: {
        0: 10, // Superscript
        1: 12, // Label
        2: 14, // Footnote
        3: 14, // BodySmall
        4: 16, // BodyMedium
        5: 18, // BodyLarge
        true: 16,
    },
    weight: fontWeightTokens,
    face: {
        // NOTE: face props are only actually applied on native
        // TODO: align font weights with the actual font files, but update actual usages
        // Desired state: default font weight is 600 (Medium), emphasized is 700 (semibold)
        // 400: { normal: FontFamilyName.PPNeueMontrealMedium },
        // 600: { normal: FontFamilyName.PPNeueMontrealSemiBold },
        // 700: { normal: FontFamilyName.PPNeueMontrealSemiBold },
        400: {
            normal: FontFamilyName.FigtreeMedium,
            italic: FontFamilyName.FigtreeMediumItalic,
        },
        500: {
            normal: FontFamilyName.FigtreeMedium,
            italic: FontFamilyName.FigtreeMediumItalic,
        },
        600: {
            normal: FontFamilyName.FigtreeSemiBold,
            italic: FontFamilyName.FigtreeSemiBoldItalic,
        },
        700: {
            normal: FontFamilyName.FigtreeSemiBold,
            italic: FontFamilyName.FigtreeSemiBoldItalic,
        },
    },
    lineHeight: {
        // 0: 12,
        // 1: 14,
        // 2: 18,
        // 3: 20,
        // 4: 22,
        // 5: 24,
        0: 12,
        1: 16,
        2: 18,
        3: 20,
        4: 24,
        5: 24,
        true: 24,
    },
    letterSpacing: {
        // 0: 0.34,
        // 1: 0.24,
        // 2: 0.14,
        // 3: 0.07,
        // 4: 0.08,
        // 5: 0.09,
        0: 0,
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0,
        true: 0,
    },
};

const monoBodyFontConfig = {
    ...bodyFontConfig,
    family: getDefaultMonoFontFamily(),
    // NOTE: face props are only actually applied on native
    // TODO: align font weights with the actual font files, but update actual usages
    // Desired state: default font weight is 600 (Medium), emphasized is 700 (semibold)
    face: {
        400: { normal: FontFamilyName.IosevkaExtendedMedium },
        500: { normal: FontFamilyName.IosevkaExtendedMedium },
    },
};

// iosevka does not support creating a variable font for web, so we have to maintain a separate font config for each weight we need
const monoBodyEmphasizedFontConfig = {
    ...bodyFontConfig,
    family: getDefaultMonoEmphasizedFontFamily(),
    // NOTE: face props are only actually applied on native
    // TODO: align font weights with the actual font files, but update actual usages
    // Desired state: default font weight is 600 (Medium), emphasized is 700 (semibold)
    face: {
        600: { normal: FontFamilyName.IosevkaExtendedSemiBold },
        700: { normal: FontFamilyName.IosevkaExtendedSemiBold },
    },
};

const headingFontConfig = {
    family: getDefaultFontFamily(),
    size: {
        // 1: 20, // H6
        // 2: 22, // H5
        // 3: 28, // H2
        // 4: 40, // H1
        // 5: 60, // Hero
        // true: 28,
        1: 20, // H6
        2: 24, // H5
        3: 32, // H2
        4: 40, // H1
        5: 56, // Hero
        true: 32,
    },
    weight: fontWeightTokens,
    face: {
        // NOTE: face props are only actually applied on native
        // TODO: align font weights with the actual font files, but update actual usages
        // Desired state: default font weight is 600 (Medium), emphasized is 700 (semibold)
        // 400: { normal: FontFamilyName.PPNeueMontrealMedium },
        // 600: { normal: FontFamilyName.PPNeueMontrealSemiBold },
        // 700: { normal: FontFamilyName.PPNeueMontrealSemiBold },
        400: { normal: FontFamilyName.FigtreeMedium },
        500: { normal: FontFamilyName.FigtreeMedium },
        600: { normal: FontFamilyName.FigtreeSemiBold },
        700: { normal: FontFamilyName.FigtreeSemiBold },
    },
    lineHeight: {
        // 1: 25,
        // 2: 28,
        // 3: 34,
        // 4: 48,
        // 5: 64,
        1: 24,
        2: 32,
        3: 40,
        4: 48,
        5: 60,
        true: 40,
    },
    letterSpacing: {
        // 1: -0.2,
        // 2: -0.22,
        // 3: -0.28,
        // 4: -0.4,
        // 5: -0.3,
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0,
        true: 0,
    },
};

export const fontTokens = {
    body: createFont({
        ...bodyFontConfig,
    }),
    monoBody: createFont({
        ...monoBodyFontConfig,
    }),
    monoBodyEmphasized: createFont({
        ...monoBodyEmphasizedFontConfig,
    }),
    heading: createFont({
        ...headingFontConfig,
    }),
};
